import { useState, useCallback } from 'react';
import {
  saveApiKey,
  getApiKey,
  removeApiKey,
  hasApiKey,
  validateApiKeyFormat,
  getApiKeyInfo,
  saveOpenAIApiKey,
  getOpenAIApiKey,
  removeOpenAIApiKey,
  hasOpenAIApiKey,
  saveAnthropicApiKey,
  getAnthropicApiKey,
  removeAnthropicApiKey,
  hasAnthropicApiKey
} from '../../../utils/storage';
import { testApiKey } from '../../../utils/openai-api';
import type { MessageState } from '../types';

interface ProviderState {
  keyValue: string;
  showKey: boolean;
  hasStoredKey: boolean;
}

export function useApiKeyManagement(
  showMessage: (type: MessageState['type'], text: string) => void,
  onApiKeyChange?: (hasKey: boolean) => void
) {
  const [isLoading, setIsLoading] = useState(false);
  const [keyInfo, setKeyInfo] = useState<{ createdAt: number; hasKey: boolean } | null>(null);

  const [openaiAdmin, setOpenaiAdmin] = useState<ProviderState>({
    keyValue: '',
    showKey: false,
    hasStoredKey: false
  });

  const [openaiStandard, setOpenaiStandard] = useState<ProviderState>({
    keyValue: '',
    showKey: false,
    hasStoredKey: false
  });

  const [anthropic, setAnthropic] = useState<ProviderState>({
    keyValue: '',
    showKey: false,
    hasStoredKey: false
  });

  const checkStoredKeys = useCallback(() => {
    const storedAdmin = hasApiKey();
    const storedOpenAI = hasOpenAIApiKey();
    const storedAnthropic = hasAnthropicApiKey();
    const info = getApiKeyInfo();

    setOpenaiAdmin(prev => ({ ...prev, hasStoredKey: storedAdmin }));
    setOpenaiStandard(prev => ({ ...prev, hasStoredKey: storedOpenAI }));
    setAnthropic(prev => ({ ...prev, hasStoredKey: storedAnthropic }));
    setKeyInfo(info);

    onApiKeyChange?.(storedAdmin);
  }, [onApiKeyChange]);

  // --- OpenAI Admin ---
  const handleSaveOpenAIAdmin = useCallback(async () => {
    const normalized = openaiAdmin.keyValue.trim();

    if (!normalized) {
      showMessage('error', 'Please enter an OpenAI Admin API key');
      return;
    }

    if (!validateApiKeyFormat(normalized)) {
      showMessage('error', 'Invalid API key format. It must start with "sk-admin-".');
      return;
    }

    setIsLoading(true);
    try {
      const testResult = await testApiKey(normalized);
      if (!testResult.success) {
        showMessage('error', `Invalid API Key: ${testResult.error}`);
        return;
      }

      if (saveApiKey(normalized)) {
        setOpenaiAdmin({ keyValue: '', showKey: false, hasStoredKey: true });
        checkStoredKeys();
        showMessage('success', 'OpenAI Admin API Key saved');
      } else {
        showMessage('error', 'Failed to save OpenAI Admin API Key');
      }
    } catch (error) {
      showMessage('error', `Failed to save API key: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [openaiAdmin.keyValue, showMessage, checkStoredKeys]);

  const handleToggleOpenAIAdmin = useCallback(() => {
    if (openaiAdmin.showKey) {
      setOpenaiAdmin(prev => ({ ...prev, showKey: false, keyValue: '' }));
      return;
    }

    const storedKey = getApiKey();
    if (storedKey) {
      setOpenaiAdmin(prev => ({ ...prev, keyValue: storedKey, showKey: true }));
    }
  }, [openaiAdmin.showKey]);

  const handleRemoveOpenAIAdmin = useCallback(() => {
    if (!window.confirm('Remove the stored OpenAI Admin API Key?')) {
      return;
    }

    if (removeApiKey()) {
      setOpenaiAdmin({ keyValue: '', showKey: false, hasStoredKey: false });
      checkStoredKeys();
      showMessage('success', 'OpenAI Admin API Key removed');
    } else {
      showMessage('error', 'Failed to remove OpenAI Admin API Key');
    }
  }, [showMessage, checkStoredKeys]);

  const handleTestOpenAIAdmin = useCallback(async () => {
    const storedKey = getApiKey();
    if (!storedKey) {
      showMessage('error', 'No OpenAI Admin API Key is stored');
      return;
    }

    setIsLoading(true);
    try {
      const result = await testApiKey(storedKey);
      if (result.success) {
        showMessage('success', 'OpenAI Admin API Key connection is valid');
      } else {
        showMessage('error', `Connection test failed: ${result.error}`);
      }
    } catch (error) {
      showMessage('error', `Connection test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [showMessage]);

  // --- OpenAI Standard ---
  const handleSaveOpenAIStandard = useCallback(() => {
    const normalized = openaiStandard.keyValue.trim();
    if (!normalized) {
      showMessage('error', 'Please enter an OpenAI API Key');
      return;
    }

    if (saveOpenAIApiKey(normalized)) {
      setOpenaiStandard({ keyValue: '', showKey: false, hasStoredKey: true });
      checkStoredKeys();
      showMessage('success', 'OpenAI API Key saved');
    } else {
      showMessage('error', 'Failed to save OpenAI API Key');
    }
  }, [openaiStandard.keyValue, showMessage, checkStoredKeys]);

  const handleToggleOpenAIStandard = useCallback(() => {
    if (openaiStandard.showKey) {
      setOpenaiStandard(prev => ({ ...prev, showKey: false, keyValue: '' }));
      return;
    }

    const key = getOpenAIApiKey();
    if (key) {
      setOpenaiStandard(prev => ({ ...prev, keyValue: key, showKey: true }));
    }
  }, [openaiStandard.showKey]);

  const handleRemoveOpenAIStandard = useCallback(() => {
    if (!window.confirm('Remove the stored OpenAI API Key?')) {
      return;
    }

    if (removeOpenAIApiKey()) {
      setOpenaiStandard({ keyValue: '', showKey: false, hasStoredKey: false });
      checkStoredKeys();
      showMessage('success', 'OpenAI API Key removed');
    } else {
      showMessage('error', 'Failed to remove OpenAI API Key');
    }
  }, [showMessage, checkStoredKeys]);

  // --- Anthropic ---
  const handleSaveAnthropic = useCallback(() => {
    const normalized = anthropic.keyValue.trim();
    if (!normalized) {
      showMessage('error', 'Please enter an Anthropic Admin API Key');
      return;
    }

    if (!normalized.startsWith('sk-ant-')) {
      showMessage('error', 'Anthropic key must start with "sk-ant-". Use an Admin API key.');
      return;
    }

    if (saveAnthropicApiKey(normalized)) {
      setAnthropic({ keyValue: '', showKey: false, hasStoredKey: true });
      checkStoredKeys();
      showMessage('success', 'Anthropic Admin API Key saved');
    } else {
      showMessage('error', 'Failed to save Anthropic Admin API Key');
    }
  }, [anthropic.keyValue, showMessage, checkStoredKeys]);

  const handleToggleAnthropic = useCallback(() => {
    if (anthropic.showKey) {
      setAnthropic(prev => ({ ...prev, showKey: false, keyValue: '' }));
      return;
    }

    const key = getAnthropicApiKey();
    if (key) {
      setAnthropic(prev => ({ ...prev, keyValue: key, showKey: true }));
    }
  }, [anthropic.showKey]);

  const handleRemoveAnthropic = useCallback(() => {
    if (!window.confirm('Remove the stored Anthropic Admin API Key?')) {
      return;
    }

    if (removeAnthropicApiKey()) {
      setAnthropic({ keyValue: '', showKey: false, hasStoredKey: false });
      checkStoredKeys();
      showMessage('success', 'Anthropic Admin API Key removed');
    } else {
      showMessage('error', 'Failed to remove Anthropic Admin API Key');
    }
  }, [showMessage, checkStoredKeys]);

  return {
    isLoading,
    keyInfo,
    checkStoredKeys,
    openaiAdmin: {
      ...openaiAdmin,
      onKeyChange: (value: string) => setOpenaiAdmin(prev => ({ ...prev, keyValue: value })),
      onToggleShow: handleToggleOpenAIAdmin,
      onSave: handleSaveOpenAIAdmin,
      onRemove: handleRemoveOpenAIAdmin,
      onTestConnection: handleTestOpenAIAdmin
    },
    openaiStandard: {
      ...openaiStandard,
      onKeyChange: (value: string) => setOpenaiStandard(prev => ({ ...prev, keyValue: value })),
      onToggleShow: handleToggleOpenAIStandard,
      onSave: handleSaveOpenAIStandard,
      onRemove: handleRemoveOpenAIStandard
    },
    anthropic: {
      ...anthropic,
      onKeyChange: (value: string) => setAnthropic(prev => ({ ...prev, keyValue: value })),
      onToggleShow: handleToggleAnthropic,
      onSave: handleSaveAnthropic,
      onRemove: handleRemoveAnthropic
    }
  };
}
